import { forwardRef, useRef, useLayoutEffect, useImperativeHandle, type InputHTMLAttributes } from 'react';

const group = (value: string) => {
  if (!value) return '';
  const [whole = '', fraction] = value.split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (fraction === undefined ? '' : `.${fraction}`);
};
export const currencyDecimal = (value: string) => value ? `${value.split('.')[0] || '0'}.${(value.split('.')[1] ?? '').padEnd(2, '0')}` : '';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string; currency?: string; onChange: (value: string) => void;
};
/** Formats the display only; the form receives an ungrouped decimal string. */
export const CurrencyInput = forwardRef<HTMLInputElement, Props>(({ value, currency = 'AUD', onChange, onBlur, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  useImperativeHandle(ref, () => inputRef.current!);
  useLayoutEffect(() => {
    if (pendingCaret.current !== null && document.activeElement === inputRef.current) inputRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [value]);
  const symbol = new Intl.NumberFormat('en-AU', { style: 'currency', currency }).formatToParts(0).find(part => part.type === 'currency')?.value ?? currency;
  return <div className="currency-input"><span aria-hidden="true">{symbol}</span><input {...props} ref={inputRef} inputMode="decimal" type="text"
    value={group(value)}
    onKeyDown={event => {
      const input = event.currentTarget, caret = input.selectionStart ?? 0;
      if (input.selectionEnd !== caret) return;
      // Delete the adjacent digit when a grouping comma sits beside the caret.
      if (event.key === 'Backspace' && input.value[caret - 1] === ',') input.setSelectionRange(caret - 1, caret - 1);
      if (event.key === 'Delete' && input.value[caret] === ',') input.setSelectionRange(caret + 1, caret + 1);
    }}
    onChange={event => {
      const input = event.currentTarget;
      const raw = input.value.replace(/[$,\s]/g, '');
      if (!/^\d*(\.\d{0,2})?$/.test(raw)) return;
      const before = input.value.slice(0, input.selectionStart ?? input.value.length).replace(/[$,\s]/g, '').length;
      // Restore the caret by counting digits and the decimal point, not inserted commas.
      const formatted = group(raw);
      let caret = 0, count = 0;
      while (caret < formatted.length && count < before) { if (formatted[caret] !== ',') count++; caret++; }
      pendingCaret.current = caret;
      onChange(raw);
    }}
    onBlur={event => { if (value) onChange(currencyDecimal(value)); onBlur?.(event); }} />
  </div>;
});
CurrencyInput.displayName = 'CurrencyInput';
